import { FC, Fragment, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { CopyIcon, CrossIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { companyIndustryTranslation } from '../../../translations/company-translation';
import {
  countryIso2TranslationKey,
  RegionGroup,
  regionTranslationKey,
} from '../../../utils/check-region-group.util';
import { TextEditor } from '../../text-editor/index';
import { PerkClaimOffer, useClaimOffer } from '../hooks/use-claim-offer.hook';
import { PerkClaimDisplayRule, PerkClaimType, perkOfferTranslationKey } from '../perks.types';
import { popupStore } from './popup.store';

export type PerkPopupProps = {};

const handlePromoCodeClick = (code: string) => {
  navigator.clipboard?.writeText?.(code);
  toast.success('Promocode copied!');
};

const perkClaimButton: Record<PerkClaimType, (payload: PerkClaimOffer) => ReactNode> = {
  [PerkClaimType.LINK]: (claim) =>
    claim.claimType === PerkClaimType.LINK ? (
      <a
        className="flex w-fit cursor-pointer items-center justify-center rounded bg-brand-700 px-4 py-3 text-start font-inter text-sm font-[550] text-white underline max-lg:w-full"
        href={claim.claimUrl.includes('https://') ? claim.claimUrl : 'https://' + claim.claimUrl}
        rel="noreferrer"
        target="_blank"
      >
        <AppFormattedMessage id="GO_TO_PARTNER_WEBSITE" />
      </a>
    ) : null,
  [PerkClaimType.PROMO_CODE]: (claim) =>
    claim.claimType === PerkClaimType.PROMO_CODE ? (
      claim.claimDisplayRule === PerkClaimDisplayRule.ON_CLICK ? (
        <Button
          className="w-fit gap-2 px-4 max-lg:w-full"
          onClick={() => handlePromoCodeClick(claim.claimCode)}
        >
          <AppFormattedMessage id="PROMOCODE" /> {claim.claimCode} <CopyIcon />
        </Button>
      ) : (
        <Button className="w-fit gap-2 px-4 max-lg:w-full">
          <AppFormattedMessage id="CHECK_YOUR_EMAIL" />
        </Button>
      )
    ) : null,
};

export const PerkPopup: FC<PerkPopupProps> = () => {
  const open = popupStore(({ isOpen }) => isOpen);
  const perk = popupStore(({ payload }) => payload);

  const close = popupStore(({ close }) => close);

  const { claimOffer, isPending, perkOffer } = useClaimOffer(perk?.id || '');

  const handleClaimClick = () =>
    claimOffer(undefined, {
      onSuccess: (claim) => {
        if (claim.claimType === PerkClaimType.LINK) {
          const href = claim.claimUrl.startsWith('http')
            ? claim.claimUrl
            : `https://${claim.claimUrl}`;
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      },
    });

  if (!perk) return null;

  const {
    banner,
    industry,
    logo,
    offerDescription,
    offerTypes,
    partnerDescription,
    partnerName,
    partnerShortDescription,
    regionsApplicable,
    url,
  } = perk;

  return (
    <ModalWrapper
      className="centered flex h-screen w-screen p-2"
      control={{ modal: true, open }}
      side="CENTER"
    >
      <div className="flex h-fit max-h-[770px] w-full max-w-[900px] flex-col overflow-y-auto rounded-lg bg-white text-xs font-[450] text-[#475467] shadow-3xl">
        <div className="relative flex h-[250px] w-full shrink-0 items-end px-8 py-6">
          <Button
            className="centered absolute right-6 top-6 z-2 flex size-9 rounded-full bg-black/20"
            onClick={close}
            styleType="NONE"
          >
            <CrossIcon iconColor="#FFFFFF" />
          </Button>
          <img className="absolute left-0 top-0 h-full w-full object-cover" src={banner} />
        </div>

        <div className="relative flex w-full flex-col gap-4 px-4 py-6">
          <div className="flex w-full items-start justify-between">
            <div className="z-1 flex flex-col gap-1">
              <span className="text-label-sm font-semibold text-[#475467]">
                <AppFormattedMessage id={companyIndustryTranslation[industry]} />
              </span>
              <span className="text-4xl font-bold text-gray-900">{partnerName}</span>
              <span className="text-xs font-[450] text-[#475467]">{partnerShortDescription}</span>
            </div>
            <div className="flex gap-2 text-nowrap text-label-sm font-semibold text-gray-400">
              {offerTypes?.map((type, i) => (
                <Fragment key={type}>
                  <AppFormattedMessage id={perkOfferTranslationKey[type]} />
                  {i % 2 === 0 && Boolean(offerTypes.length > 1) && <span>|</span>}
                </Fragment>
              ))}
            </div>
          </div>
          <div
            className={twMerge(
              'flex items-center',
              regionsApplicable.length ? 'justify-between' : 'justify-end',
            )}
          >
            {Boolean(regionsApplicable.length) && (
              <div className="flex-cpl flex flex-col gap-2">
                <span className="text-m font-semibold text-black">Applicable for</span>
                <div className="flex flex-wrap gap-1">
                  {regionsApplicable.map((iso2, i) => (
                    <span key={iso2}>
                      {regionTranslationKey[iso2 as RegionGroup] || countryIso2TranslationKey[iso2]}
                      {i !== regionsApplicable.length - 1 && ','}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <span className="text-m font-semibold text-black">
            <AppFormattedMessage id="PERK_ABOUT_COMPANY" />
          </span>
          <TextEditor disabled value={partnerDescription} />
          <span className="text-m font-semibold text-black">
            <AppFormattedMessage id="PERK_OFFER_DETAILS" />
          </span>
          <TextEditor disabled value={offerDescription} />
          <div className="sticky bottom-0 flex h-20 w-full items-center justify-between bg-white pt-4 max-lg:h-fit max-lg:flex-col max-lg:items-start max-lg:gap-2">
            <div className="flex w-fit items-center gap-2">
              <img className="size-8 rounded-full object-cover" src={logo} />
              <a
                className="text-wrap text-start text-xs font-[450] text-brand-700 underline"
                href={url.startsWith('http') ? url : 'https://' + url}
                rel="noreferrer"
                target="_blank"
              >
                {partnerName}
              </a>
            </div>

            {perkOffer ? (
              perkClaimButton[perkOffer.claimType](perkOffer)
            ) : (
              <Button
                className="w-fit px-4 max-lg:w-full"
                isLoading={isPending}
                onClick={handleClaimClick}
              >
                <AppFormattedMessage id="PERK_CLAIM_THIS_OFFER" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
